import dialog from './Dialog';
import dataLayerPush from './DataLayer';

export default function addPackageToCart({
  title,
  code,
  category,
  packageLimit,
  packageAmountPerProduct,
  productIds,
  salePrice,
}: {
  title: string,
  code: string,
  category: string,
  packageLimit: number,
  packageAmountPerProduct: number,
  productIds: number[],
  salePrice: number,
}) {
  const qty: Record<number, number> = productIds
    .reduce((acc: Record<number, number>, productId) => {
      acc[productId] = 0;
      return acc;
    }, {});

  return {
    ...dialog(),

    submitting: false,
    timestamp: Date.now(),
    qty,
    totalSelected: 0,

    handleInit() {
      dataLayerPush({
        event: 'view_item',
        ecommerce: {
          send_to: 'GA4',
          currency: 'EUR',
          value: salePrice,
          items: [{
            item_brand: 'Driessen stoffen',
            item_name: title,
            item_id: code,
            item_category: category,
            price: salePrice,
          }],
        },
      });
    },

    handleQtyIncrease(productId: number) {
      const currentQty = this.qty[productId];
      if (currentQty >= packageLimit) {
        return;
      }
      this.qty[productId] = currentQty + 1;
      this.totalSelected = Object.values(this.qty).reduce((sum, quantity) => sum + quantity, 0);
    },

    handleQtyDecrease(productId: number) {
      const currentQty = this.qty[productId];
      if (currentQty < 1) {
        return;
      }
      this.qty[productId] = currentQty - 1;
      this.totalSelected = Object.values(this.qty).reduce((sum, quantity) => sum + quantity, 0);
    },

    handleQtyInput(productId: number) {
      const currentQty = this.qty[productId];
      this.qty[productId] = Math.min(Math.max(currentQty, 0), packageLimit - this.totalSelected);
      this.totalSelected = Object.values(this.qty).reduce((sum, quantity) => sum + quantity, 0);
    },

    handleSubmit(event: FormDataEvent) {
      if (this.submitting) {
        return;
      }
      this.submitting = true;
      dataLayerPush({
        event: 'add_to_cart',
        ecommerce: {
          currency: 'EUR',
          value: salePrice,
          items: [{
            item_brand: 'Driessen stoffen',
            item_name: title,
            item_id: code,
            item_category: category,
            price: salePrice,
          }],
        },
        eventTimeout: 2000,
      });

      const form = event.target as HTMLFormElement;
      fetch('/', {
        method: 'POST',
        body: new FormData(form),
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        redirect: 'manual',
      })
        .then(() => {
          this.submitting = false;
          // @ts-ignore
          htmx.trigger('#account-menu', 'refresh');
          // @ts-ignore
          htmx.trigger('#account-menu-mobile', 'refresh');
          this.openSuccessDialog();
        })
        .catch(() => {
          this.submitting = false;
        });
    },

    resetForm() {
      Object.keys(this.qty).forEach((key) => {
        this.qty[Number(key)] = 0;
      });
      this.totalSelected = 0;
      this.timestamp = Date.now();
    },

    openSuccessDialog() {
      this.openDialog();
    },

    closeSuccessDialog() {
      this.resetForm();
      this.closeDialog();
    },

    isProductSelected(productId: number) {
      return this.qty[productId] > 0;
    },

    productAmount(productId: number) {
      return packageAmountPerProduct * this.qty[productId];
    },

    isPackagePrepared() {
      return this.totalSelected === packageLimit;
    },

    isPackageOverLimit() {
      return this.totalSelected > packageLimit;
    },

    dropdownText() {
      if (this.isPackagePrepared()) {
        return Craft.t('product', 'Your package is ready');
      }
      if (this.isPackageOverLimit()) {
        return Craft.t(
          'product',
          'Select maximum {qty} products',
          { qty: packageLimit },
        );
      }

      return this.totalSelected > 0
        ? Craft.t(
          'product',
          'Select {qty, plural, =1{1 more product} other{# more products}}',
          { qty: packageLimit - this.totalSelected },
        )
        : Craft.t('product', 'Select products');
    },

    totalFormatted() {
      return Craft.formatCurrency(salePrice);
    },
  };
}
